import type { ReactNode } from 'react';
import { Text, Popover, Stack, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import { QuestionTypes } from '../../../../../domain/Surveys/Questions';
import type {
  NextQuestion,
  SurveyQuestion,
} from '../../../../../domain/Surveys/Questions/SurveyQuestion';

const PopoverTrigger = styled.span`
  border-bottom: 1px dotted ${tokens.color.text_light};
  cursor: help;
`;

const StyledPopover = styled(Popover)`
  white-space: pre-line;
`;

export const SelectContent = styled(Stack)`
  text-align: start;
  align-items: center;
  overflow: hidden;
`;

export const SelectContentText = styled(Text)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export type SpecificQuestion = { type: 'survey-question'; question: SurveyQuestion };

export type ByOrder = { type: 'by-order' };

export type ThankYou = { type: 'thank-you' };

export type BaseQuestionOption = SpecificQuestion | ByOrder | ThankYou;

export type BasedOnAnswer = { type: 'based-on-answer'; options: BaseQuestionOption[] };

export type NextQuestionOption = BasedOnAnswer | BaseQuestionOption;

export type SkippedNextQuestionOption = BaseQuestionOption;

export type AnswerLabel = { content?: ReactNode; text: string };

const TRANSLATION_PREFIX = 'feedback:wizard.section.questions.next-question';
const NPS_READ_MORE_URL = '/articles/360025960953';

export function useAnswerLabels(question: SurveyQuestion): AnswerLabel[] {
  const { t } = useTranslationWithPrefix(`${TRANSLATION_PREFIX}.based-on-answer`);
  const helpHotjarUrl = useHelpHotjarUrl();

  switch (question.type) {
    case QuestionTypes.SINGLE_CLOSE_ENDED:
      return question.answers.map((_, index) => ({
        text: t('radio.label', { option: index + 1 }),
        content: (
          <Text data-testid={`radio-answer-${index + 1}`}>
            {t('radio.content-1')}
            <Text textType="subtitle2">{t('radio.label', { option: index + 1 })}</Text>
            {t('radio.content-2')}
          </Text>
        ),
      }));
    case QuestionTypes.RATING_SCALE_5:
    case QuestionTypes.REACTION:
      return [
        {
          text: `${t('rating.negative')} ${t(
            question.type === QuestionTypes.REACTION ? 'range-or-option' : 'range-or',
            { min: 1, max: 2 },
          )}`,
          content: (
            <Text data-testid="rating-5-negative">
              {t('rating.content')}
              <Text textType="subtitle2">{t('rating.negative')}</Text>{' '}
              {t(question.type === QuestionTypes.REACTION ? 'range-or-option' : 'range-or', {
                min: 1,
                max: 2,
              })}
            </Text>
          ),
        },
        {
          text: `${t('rating.neutral')} (${
            question.type === QuestionTypes.REACTION ? `${t('option')} ` : ''
          }3)`,
          content: (
            <Text data-testid="rating-5-neutral">
              {t('rating.content')}
              <Text textType="subtitle2">{t('rating.neutral')}</Text>
              {` (${question.type === QuestionTypes.REACTION ? `${t('option')} ` : ''}3)`}
            </Text>
          ),
        },
        {
          text: `${t('rating.positive')} ${t(
            question.type === QuestionTypes.REACTION ? 'range-or-option' : 'range-or',
            { min: 4, max: 5 },
          )}`,
          content: (
            <Text data-testid="rating-5-positive">
              {t('rating.content')}
              <Text textType="subtitle2">{t('rating.positive')}</Text>{' '}
              {t(question.type === QuestionTypes.REACTION ? 'range-or-option' : 'range-or', {
                min: 4,
                max: 5,
              })}
            </Text>
          ),
        },
      ];
    case QuestionTypes.RATING_SCALE_7:
      return [
        {
          text: `${t('rating.negative')} ${t('range-to', { min: 1, max: 3 })}`,
          content: (
            <Text data-testid="rating-7-negative">
              {t('rating.content')}
              <Text textType="subtitle2">{t('rating.negative')}</Text>{' '}
              {t('range-to', { min: 1, max: 3 })}
            </Text>
          ),
        },
        {
          text: `${t('rating.neutral')} (4)`,
          content: (
            <Text data-testid="rating-7-neutral">
              {t('rating.content')}
              <Text textType="subtitle2">{t('rating.neutral')}</Text>
              {' (4)'}
            </Text>
          ),
        },
        {
          text: `${t('rating.positive')} ${t('range-to', { min: 5, max: 7 })}`,
          content: (
            <Text data-testid="rating-7-positive">
              {t('rating.content')}
              <Text textType="subtitle2">{t('rating.positive')}</Text>{' '}
              {t('range-to', { min: 5, max: 7 })}
            </Text>
          ),
        },
      ];
    case QuestionTypes.NET_PROMOTER_SCORE:
      return [
        {
          text: t('nps.detractors'),
          content: (
            <Text data-testid="nps-detractors">
              {t('nps.content')}
              <Text textType="subtitle2">
                <StyledPopover
                  title={`NPS® ${t('nps.detractors')} ${t('range-to', { min: 0, max: 6 })}`}
                  text={t('nps.detractors-info')}
                  actionText={t('nps.tooltip-action')}
                  onActionPress={() => {
                    window.open(`${helpHotjarUrl}${NPS_READ_MORE_URL}`, '_blank');
                  }}
                  icon="info"
                >
                  <PopoverTrigger>{t('nps.detractors')}</PopoverTrigger>
                </StyledPopover>
              </Text>
              {` ${t('range-to', { min: 0, max: 6 })}`}
            </Text>
          ),
        },
        {
          text: t('nps.passives'),
          content: (
            <Text data-testid="nps-passives">
              {t('nps.content')}
              <Text textType="subtitle2">
                <StyledPopover
                  title={`NPS® ${t('nps.passives')} ${t('range-or', { min: 7, max: 8 })}`}
                  text={t('nps.passives-info')}
                  actionText={t('nps.tooltip-action')}
                  onActionPress={() => {
                    window.open(`${helpHotjarUrl}${NPS_READ_MORE_URL}`, '_blank');
                  }}
                  icon="info"
                >
                  <PopoverTrigger>{t('nps.passives')}</PopoverTrigger>
                </StyledPopover>
              </Text>
              {` ${t('range-or', { min: 7, max: 8 })}`}
            </Text>
          ),
        },
        {
          text: t('nps.promoters'),
          content: (
            <Text data-testid="nps-promoters">
              {t('nps.content')}
              <Text textType="subtitle2">
                <StyledPopover
                  title={`NPS® ${t('nps.promoters')} ${t('range-or', { min: 9, max: 10 })}`}
                  text={t('nps.promoters-info')}
                  actionText={t('nps.tooltip-action')}
                  onActionPress={() => {
                    window.open(`${helpHotjarUrl}${NPS_READ_MORE_URL}`, '_blank');
                  }}
                  icon="info"
                >
                  <PopoverTrigger>{t('nps.promoters')}</PopoverTrigger>
                </StyledPopover>
              </Text>
              {` ${t('range-or', { min: 9, max: 10 })}`}
            </Text>
          ),
        },
      ];
    default:
      return [];
  }
}

export function getOption(questions: SurveyQuestion[], next: NextQuestion): BaseQuestionOption {
  if (next === 'byOrder') return { type: 'by-order' };
  if (next === 'thankYou') return { type: 'thank-you' };

  const uuid = next.replace('question:', '');
  const foundQuestion = questions.find((q) => q.uuid === uuid);

  if (foundQuestion === undefined) return { type: 'by-order' };

  return { type: 'survey-question', question: foundQuestion };
}

export function useNextQuestionOption(
  questions: SurveyQuestion[],
  question: SurveyQuestion,
): NextQuestionOption {
  const answerLabels = useAnswerLabels(question);
  if (question.next === 'byAnswer' && 'nextByAnswer' in question) {
    return {
      type: 'based-on-answer',
      options: answerLabels.map((_, index) => {
        if (question.nextByAnswer === undefined || question.nextByAnswer[index] === undefined)
          return getOption(questions, 'byOrder');
        return getOption(questions, question.nextByAnswer[index]);
      }),
    };
  }

  return getOption(questions, question.next);
}

export function useQuestionLabel(): (
  questions: SurveyQuestion[],
  option: NextQuestionOption,
) => string {
  const { t } = useTranslationWithPrefix(`${TRANSLATION_PREFIX}.label`);
  return (questions, option) => {
    // eslint-disable-next-line default-case
    switch (option.type) {
      case 'by-order':
        return t('by-order');
      case 'thank-you':
        return t('thank-you');
      case 'survey-question':
        return t('survey-question', {
          questionText: `${questions.indexOf(option.question) + 1}: ${option.question.text}`,
        });
      case 'based-on-answer':
        return t('based-on-answer');
    }
  };
}
